import { useUserTier } from '@hooks/useUserTier';
import { useStore } from '@store/index';
import { buildOrgRoutePath, buildRoutePath } from '@utils/routes';
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';

import CasesContainer from './CasesContainer';
import { getCasesPageForTier } from './tierRegistry';

/**
 * CasesRouter - Tier-aware router for the cases list route.
 *
 * Resolves the current user's tier (derived from their RBAC permissions) and renders
 * the tier-specific cases page from the registry, falling back to the default
 * CasesContainer. Gates on the tier loading state so a Tier 2 user never briefly sees
 * the standard page before their permissions resolve.
 *
 * Mounted on two routes:
 *  - org-scoped `/org-{id}/cases` (no `:progId`) — the Tier-2 org-wide entry.
 *  - program-scoped `/org-{id}/prog-{id}/cases` — the standard per-program entry.
 *
 * On the org route, only Tier-2 makes sense (CasesContainer requires a selected
 * program), so any non-tier2 user is redirected to their program cases route.
 *
 * Mirrors the workflow-based registry/router pattern used by CaseDetailsRouter.
 */
export const CasesRouter: React.FC = () => {
  const { tier, isLoading } = useUserTier();
  const { progId } = useParams<{ progId?: string }>();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const isOrgRoute = !progId;

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        <p className="mt-2 text-sm text-muted-foreground">Loading cases...</p>
      </div>
    );
  }

  // The org-scoped route only serves Tier-2 (org-wide). A non-tier2 user landing
  // here (e.g. direct URL) is sent to their program cases route — CasesContainer
  // depends on a selected program and must not render program-less.
  if (isOrgRoute && tier !== 'tier2') {
    return selectedOrganizationId && selectedProgramId ? (
      <Navigate to={buildRoutePath('/cases', selectedOrganizationId, selectedProgramId)} replace />
    ) : (
      <Navigate to="/" replace />
    );
  }

  // Mirror guard: Tier-2's cases list is org-wide, so the program-scoped list
  // route is the wrong place for them. Many secondary entry points still build
  // the program-scoped path (breadcrumbs, post-action redirects, permission
  // fallbacks); rather than make each tier-aware, funnel them here — bounce a
  // Tier-2 user from the program list route to the org cases route. (Disjoint
  // from the guard above; case detail is a sibling route, so it is unaffected.)
  if (!isOrgRoute && tier === 'tier2' && selectedOrganizationId) {
    return <Navigate to={buildOrgRoutePath('/cases', selectedOrganizationId)} replace />;
  }

  const PageComponent = getCasesPageForTier(tier) ?? CasesContainer;

  return <PageComponent />;
};
